<script setup>
// Nagłówek strony wyników: logo organizatora, nazwa i data wydarzenia, stan (przed startem / na żywo /
// zakończone), trasy tych samych zawodów i „Śledź na mapie” (beta). Makiety: „Wyniki – makiety”, W1/W2/H.
import { computed, defineProps, ref, watch, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import { API_URL } from '../../config.js'
import { useNowTick } from '../../utils/now-tick.js'
import { formatClock } from '../../utils/polish-time.js'
import { betaEventUrl, eventDateText, eventLogoUrl, eventPhase, routeLabels, startsInText } from '../../utils/event-info.js'

const props = defineProps({
  event: { type: Object, default: null },
  alias: { type: String, required: true },
  lastUpdate: { type: Number, default: null },
  // szersza kolumna treści (1376 px zamiast 1120) — /ar z kolumnami etapów (makieta AR1)
  wide: { type: Boolean, default: false }
})

// dane starsze niż to w trakcie wydarzenia = „Brak połączenia” (odświeżanie co 30 s)
const STALE_MS = 90 * 1000

const route = useRoute()
const now = useNowTick()
const logoFailed = ref(false)
const logoUrl = computed(() => (logoFailed.value ? '' : eventLogoUrl(props.event)))
const name = computed(() => props.event?.name || props.alias)
const dateText = computed(() => eventDateText(props.event))
const phase = computed(() => eventPhase(props.event, now.value))
const startsIn = computed(() => startsInText(props.event, now.value))
const stale = computed(() => phase.value === 'live' && props.lastUpdate != null && now.value - props.lastUpdate > STALE_MS)
const updatedAt = computed(() => (props.lastUpdate != null ? formatClock(props.lastUpdate) : ''))
const mapUrl = computed(() => betaEventUrl(props.alias))
const barLogo = `${process.env.BASE_URL || '/'}favicon.svg`

// Trasy tych samych zawodów (competition_id): GET /events?visible=1&competition_id=N, jak w becie.
// Starsze API ignoruje competition_id i zwraca wszystko — dlatego filtr też tutaj.
const routes = ref([])
const routesOpen = ref(false)
const routeItems = computed(() => {
  const labels = routeLabels(routes.value)
  return routes.value.map((e, i) => ({ alias: e.alias, label: labels[i], current: e.alias === props.alias }))
})
const currentRouteLabel = computed(() => routeItems.value.find(r => r.current)?.label || '')

watch(() => props.event?.competition_id, async competitionId => {
  routes.value = []
  if (!competitionId) return
  try {
    const response = await fetch(`${API_URL}/api/v1/events?visible=1&competition_id=${encodeURIComponent(competitionId)}`)
    const data = await response.json()
    const list = (Array.isArray(data) ? data : []).filter(e => e && e.alias && String(e.competition_id) === String(competitionId) && (e.visible === undefined || e.visible === true || e.visible === 1))
    routes.value = list.length > 1 ? list.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'pl')) : []
  } catch (error) {
    console.warn('Nie udało się pobrać tras zawodów', error)
  }
}, { immediate: true })

function closeRoutes(event) {
  if (!event.target.closest || !event.target.closest('.eh-routes')) routesOpen.value = false
}
watch(routesOpen, open => {
  if (open) document.addEventListener('click', closeRoutes)
  else document.removeEventListener('click', closeRoutes)
})
onBeforeUnmount(() => document.removeEventListener('click', closeRoutes))

function routeHref(alias) {
  return route.path.replace(/[^/]+$/, encodeURIComponent(alias))
}
</script>

<template>
  <div class="eh-bar">
    <img class="eh-bar__logo" :src="barLogo" alt="" width="24" height="24" />
    <span>Wyniki ŚledźGPS</span>
  </div>
  <header class="eh" :class="{ 'eh--wide': wide }">
    <div class="eh__inner">
      <div class="eh__main">
        <span v-if="logoUrl" class="eh__logo">
          <img :src="logoUrl" :alt="`Logo: ${name}`" @error="logoFailed = true" />
        </span>
        <div class="eh__text">
          <h1 class="eh__name">{{ name }}</h1>
          <p v-if="dateText" class="eh__date">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>
            {{ dateText }}
          </p>
          <div class="eh__status">
            <span v-if="phase === 'live' && stale" class="eh-chip eh-chip--warn"><span class="eh-chip__dot" aria-hidden="true" />Brak połączenia</span>
            <span v-else-if="phase === 'live'" class="eh-chip eh-chip--live"><span class="eh-chip__dot" aria-hidden="true" />Na żywo</span>
            <span v-else-if="phase === 'before'" class="eh-chip eh-chip--before"><span class="eh-chip__dot" aria-hidden="true" />Przed startem<template v-if="startsIn"> · {{ startsIn }}</template></span>
            <span v-else-if="phase === 'ended'" class="eh-chip eh-chip--ended"><span class="eh-chip__dot" aria-hidden="true" />Zakończone</span>
            <span v-if="phase === 'live' && updatedAt" class="eh__updated">
              <template v-if="stale">dane z {{ updatedAt }}</template>
              <template v-else><span class="eh__updated-long">aktualizacja </span><span class="eh__updated-short">akt. </span>{{ updatedAt }}<span class="eh__updated-long"> · co 30 s</span></template>
            </span>
            <div v-if="routeItems.length" class="eh-routes">
              <button type="button" class="eh-routes__btn" aria-haspopup="listbox" :aria-expanded="routesOpen ? 'true' : 'false'" @click="routesOpen = !routesOpen">
                {{ currentRouteLabel || 'Trasa' }}
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
              </button>
              <ul v-if="routesOpen" class="eh-routes__menu" role="listbox" aria-label="Trasy zawodów">
                <li v-for="item in routeItems" :key="item.alias" role="option" :aria-selected="item.current ? 'true' : 'false'">
                  <a :href="routeHref(item.alias)" :class="{ 'is-current': item.current }">{{ item.label }}</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <a v-if="mapUrl" class="eh__map" :href="mapUrl" target="_blank" rel="noopener" aria-label="Śledź na mapie (otwiera się w nowej karcie)">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 4L3 6v14l6-2 6 2 6-2V4l-6 2z" /><path d="M9 4v14M15 6v14" /></svg>
          <span class="eh__map-text">Śledź na mapie</span>
          <svg class="eh__map-ext" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M14 4h6v6" /><path d="M20 4l-9 9" /><path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" /></svg>
        </a>
      </div>
    </div>
  </header>
</template>

<style scoped>
.eh-bar {
  height: 44px;
  padding: 0 24px;
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--sledz-surface);
  border-bottom: 1px solid var(--sledz-border-soft);
  font-size: 13.5px;
  font-weight: 700;
  color: var(--sledz-text-secondary);
}
.eh-bar__logo {
  border-radius: 6px;
}
.eh {
  background: var(--sledz-surface);
  border-bottom: 1px solid var(--sledz-border-soft);
}
.eh__inner {
  max-width: 1120px;
  margin: 0 auto;
  padding: 24px 24px 22px;
}
.eh--wide .eh__inner {
  max-width: 1376px;
}
.eh__main {
  display: flex;
  align-items: center;
  gap: 20px;
}
.eh__logo {
  flex: none;
  width: 96px;
  height: 96px;
  border-radius: 16px;
  background: var(--sledz-surface);
  border: 1px solid var(--sledz-border-soft);
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.eh__logo img {
  max-width: 84px;
  max-height: 84px;
  object-fit: contain;
}
.eh__text {
  flex: 1;
  min-width: 0;
}
.eh__name {
  margin: 0;
  font-size: 30px;
  line-height: 1.15;
  font-weight: 800;
  letter-spacing: -0.3px;
  color: var(--sledz-text);
  overflow-wrap: anywhere;
}
.eh__date {
  margin: 6px 0 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  color: var(--sledz-text-secondary);
}
.eh__date svg {
  flex: none;
}
.eh__status {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.eh-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 11px;
  border-radius: 14px;
  font-size: 13px;
  font-weight: 800;
  white-space: nowrap;
}
.eh-chip__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
}
.eh-chip--live {
  background: #fee2e2;
  color: var(--sledz-live-strong);
}
.eh-chip--live .eh-chip__dot {
  background: var(--sledz-live);
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.18);
}
.eh-chip--before {
  background: var(--sledz-primary-soft);
  color: var(--sledz-primary-deep);
}
.eh-chip--ended {
  background: #eef2f7;
  color: var(--sledz-text-secondary);
}
.eh-chip--ended .eh-chip__dot {
  background: var(--sledz-text-muted);
}
.eh-chip--warn {
  background: var(--sledz-warning-soft);
  color: var(--sledz-warning-ink);
}
.eh-chip--warn .eh-chip__dot {
  background: var(--sledz-warning);
}
.eh__updated {
  font-size: 13.5px;
  color: var(--sledz-text-muted);
  font-variant-numeric: tabular-nums;
}
.eh__updated-short {
  display: none;
}
.eh-routes {
  position: relative;
}
.eh-routes__btn {
  height: 32px;
  padding: 0 10px 0 12px;
  border: 1px solid var(--sledz-border);
  border-radius: 16px;
  background: var(--sledz-surface);
  color: var(--sledz-text);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13.5px;
  font-weight: 700;
  white-space: nowrap;
}
.eh-routes__menu {
  position: absolute;
  z-index: 30;
  top: calc(100% + 6px);
  left: 0;
  min-width: 200px;
  margin: 0;
  padding: 6px;
  list-style: none;
  background: var(--sledz-surface);
  border: 1px solid var(--sledz-border-soft);
  border-radius: 12px;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.16);
}
.eh-routes__menu a {
  display: block;
  padding: 9px 12px;
  border-radius: 8px;
  color: var(--sledz-text);
  font-size: 14.5px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
}
.eh-routes__menu a:hover,
.eh-routes__menu a.is-current {
  background: var(--sledz-primary-soft);
  color: var(--sledz-primary-deep);
}
.eh__map {
  flex: none;
  height: 40px;
  padding: 0 14px;
  border-radius: 20px;
  border: 1.5px solid var(--sledz-primary);
  background: var(--sledz-surface);
  color: var(--sledz-primary-deep);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 800;
}
.eh__map:hover {
  background: var(--sledz-primary-soft);
}

@media (max-width: 767.98px) {
  .eh-bar {
    display: none;
  }
  .eh__inner {
    padding: 14px 16px 12px;
  }
  .eh__main {
    align-items: flex-start;
    gap: 12px;
  }
  .eh__logo {
    width: 60px;
    height: 60px;
    border-radius: 12px;
  }
  .eh__logo img {
    max-width: 52px;
    max-height: 52px;
  }
  .eh__name {
    font-size: 19px;
    line-height: 1.2;
  }
  .eh__date {
    margin-top: 3px;
    font-size: 13px;
  }
  .eh__date svg {
    display: none;
  }
  .eh__status {
    margin-top: 8px;
  }
  .eh__updated {
    font-size: 12.5px;
  }
  .eh__updated-long {
    display: none;
  }
  .eh__updated-short {
    display: inline;
  }
  .eh-routes__btn {
    height: 28px;
    border-radius: 14px;
  }
  .eh__map {
    width: 40px;
    padding: 0;
    justify-content: center;
    border-width: 1px;
    border-color: var(--sledz-border);
    color: var(--sledz-primary-strong);
  }
  .eh__map-text,
  .eh__map-ext {
    display: none;
  }
}
</style>
